import { useEffect, useState } from "react";

import { Box, Button } from "@mui/material";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Link } from "react-router-dom";
import { PRIVATE_PAGES } from "@app/router/pages";
import { useDeleteMarketMutation, useLazyGetMarketsQuery } from "@modules/market";
import { IMarket } from "@modules/market/types";
import { ApiEndpoint } from "@constants/index";
import DeleteButton from "common/DeleteButton";
import SortableList, { SortableItem } from "react-easy-sort";
import { arrayMoveImmutable } from "array-move";
import { useSortMarketsMutation } from "@modules/market/api/market.api";

import { StyledImg } from "./styled";

const Market = () => {
  const [markets, setMarkets] = useState<IMarket[]>([])
  const [getMarkets] = useLazyGetMarketsQuery()
  const [deleteMarket] = useDeleteMarketMutation()
  const [sortMarkets] = useSortMarketsMutation()
  const [copiedMarketId, setCopiedMarketId] = useState<number | null>(null)

  const handleGetRequest = async () => {
    try {
      const marketsResponse = await getMarkets({ offset: 0, limit: 100 }).unwrap();

      setMarkets(marketsResponse?.markets || [])
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteMarket = async (id: string) => {
    if (!id) {
      return null
    }

    try {
      await deleteMarket({ id })
      handleGetRequest()
    } catch (error) {
      console.log(error);
    }
  }

  const sendSortToBackend = async (sortedList: IMarket[]) => {
    const payload = sortedList.map(({ id }, index) => ({ id, index }));

    await sortMarkets({ list: payload })
  }

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    const clone = JSON.parse(JSON.stringify(markets)) as IMarket[]

    const sortedList = arrayMoveImmutable(clone, oldIndex, newIndex)

    setMarkets(sortedList)
    sendSortToBackend(sortedList)
  }

  const handleCopyImportMarketId = async (importMarketId: number) => {
    await navigator.clipboard.writeText(String(importMarketId))
    setCopiedMarketId(importMarketId)

    setTimeout(() => setCopiedMarketId(null), 1500)
  }

  useEffect(() => {
    handleGetRequest()
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '20px',
        flexDirection: 'column',
        alignItems: 'flex-end'
      }}
    >
      <Link to={PRIVATE_PAGES.CREATE_MARKETS}>
        <Button variant="contained" color="primary">
          Create Market
        </Button>
      </Link>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow
              sx={{
                display: 'grid',
                gridTemplateColumns: '60px 140px 100px 2fr 100px 160px',
                alignItems: 'center',
              }}
            >
              <TableCell align="center">#</TableCell>
              <TableCell align="center">Market ID</TableCell>
              <TableCell align="center">Image</TableCell>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Visible</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <SortableList
              onSortEnd={onSortEnd}
              lockAxis="y"
              draggedItemClassName="dragged"
            >
              {markets.map(({
                id,
                name,
                visible,
                images,
                importMarketId,
              }, index) => {
                return (
                  <SortableItem
                    key={id}
                  >
                    <TableRow
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '60px 140px 100px 2fr 100px 160px',
                        alignItems: 'center',
                        cursor: 'grab',
                        userSelect: 'none',
                        '&.dragged': {
                          background: '#f0f7ff',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          zIndex: 10,
                        },
                      }}
                    >
                      <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Box component="span" sx={{ textDecoration: importMarketId !== null ? 'underline' : 'none' }}>
                            {importMarketId ?? '-'}
                          </Box>
                          {importMarketId !== null && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleCopyImportMarketId(importMarketId)}
                            >
                              {copiedMarketId === importMarketId ? 'Copied' : 'Copy'}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {images?.[0]?.url && <StyledImg src={`${ApiEndpoint}/${images[0].url}`} alt="" />}
                      </TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">{visible ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="center">
                        <Link to={`/markets/${id}`}>
                          <Button variant="outlined" size="small">
                            Edit
                          </Button>
                        </Link>
                        <DeleteButton
                          callBack={() => {
                            handleDeleteMarket(id)
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </SortableItem>
                )
              })}
            </SortableList>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
};

export default Market;
